import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './Notifier.css';
import alertGraphic from './res/Alert-graphic.svg';

/**
 * Props:
 * - message: string         // each non-empty message is appended
 * - type: 'info'|'success'|'warn'|'error'
 * - signal: number          // bump this for every notify to force-append (even if text repeats)
 * - durationMs: number      // auto-close delay; restarts on each new message
 * - onClose: () => void     // called after board fully closes
 */
const NotifierContent = ({ message, type, signal, durationMs = 5000, onClose }) => {
  const [isMounted, setIsMounted] = useState(false);
  const [phase, setPhase] = useState('idle'); // 'idle' | 'entering' | 'visible' | 'leaving'
  const [messages, setMessages] = useState([]);

  const timerRef = useRef(null);
  const hoveredRef = useRef(false);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const startTimer = useCallback(() => {
    clearTimer();
    if (!hoveredRef.current && durationMs > 0) {
      timerRef.current = setTimeout(() => {
        setPhase('leaving');
      }, durationMs);
    }
  }, [durationMs]);

  // Append & restart timer when a new message arrives.
  // Including `signal` guarantees an append per notify call, even if texts repeat or React batches updates.
  useEffect(() => {
    const txt = typeof message === 'string' ? message.trim() : '';
    if (!txt) return;

    setMessages(prev => [...prev, { text: txt, type: type || 'info' }]);

    setIsMounted(true);
    setPhase(prev => (prev === 'idle' ? 'entering' : 'visible'));

    clearTimer();
    const id = setTimeout(() => {
      if (!hoveredRef.current) startTimer();
    }, 0);

    return () => clearTimeout(id);
  }, [message, type, signal, startTimer]);

  // Manage timer lifecycle based on phase
  useEffect(() => {
    if (phase === 'visible') startTimer();
    return clearTimer;
  }, [phase, startTimer]);

  const handleAnimationEnd = () => {
    if (phase === 'entering') {
      setPhase('visible');
    } else if (phase === 'leaving') {
      setIsMounted(false);
      setPhase('idle');
      setMessages([]); // clear stack on close
      if (typeof onClose === 'function') onClose();
    }
  };

  if (!isMounted) return null;

  return (
    <div
      className={[
        'noticeboard',
        phase === 'entering' ? 'entering' : '',
        phase === 'visible' ? 'shown' : '',
        phase === 'leaving' ? 'leaving' : '',
      ].filter(Boolean).join(' ')}
      onAnimationEnd={handleAnimationEnd}
      onMouseEnter={() => { hoveredRef.current = true; clearTimer(); }}
      onMouseLeave={() => { hoveredRef.current = false; startTimer(); }}
    >
      <div className="noticeboard-inner02">
        <img src={alertGraphic} alt="Alert" className="alert-graphic" />
        <button className="close-btn" onClick={() => setPhase('leaving')}>×</button>
      </div>

      <div className="noticeboard-inner01">
        {messages.map((msg, index) => (
          <div key={index} className={`messagebox ${msg.type || 'info'}`}>
            {msg.text}
          </div>
        ))}
      </div>
    </div>
  );
};

const Notifier = (props) => {
  return ReactDOM.createPortal(<NotifierContent {...props} />, document.body);
};

export default Notifier;
